import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Input, Select, Tag, Space, Modal,
  Form, notification, Switch, Row, Col, Descriptions, Badge, Statistic,
  Tooltip, Upload,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  SearchOutlined, CheckCircleOutlined, CloseCircleOutlined,
  LinkOutlined, CommentOutlined, UploadOutlined, StarOutlined,
  BookOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const VEDAS = ['Rigveda', 'Samaveda', 'Yajurveda', 'Atharvaveda'];

// ── Sub-components ────────────────────────────────────────────────────────────

function StatsRow({ stats }) {
  if (!stats) return null;
  const { gotras, kuldevis, kulrishis, comments, contributions } = stats;
  const items = [
    { label: 'Gotras (Approved)', value: gotras?.approved || 0, color: '#7a0000' },
    { label: 'Gotras (Pending)', value: gotras?.pending || 0, color: '#d97706' },
    { label: 'Kuldevis (Approved)', value: kuldevis?.approved || 0, color: '#7c3aed' },
    { label: 'Kuldevis (Pending)', value: kuldevis?.pending || 0, color: '#d97706' },
    { label: 'Kulrishis', value: kulrishis?.total || 0, color: '#0891b2' },
    { label: 'Pending Contributions', value: contributions?.pending || 0, color: '#dc2626' },
    { label: 'Pending Comments', value: comments?.pending || 0, color: '#dc2626' },
  ];
  return (
    <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
      {items.map(s => (
        <Col key={s.label} xs={12} sm={8} md={6} lg={4}>
          <Card size="small" style={{ borderLeft: `4px solid ${s.color}`, borderRadius: 10 }} bodyStyle={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{s.label}</div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

// ── Gotra Tab ─────────────────────────────────────────────────────────────────
function GotrasTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();

  const fetch = useCallback(async (p = 1, s = search, st = statusFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50, ...(s ? { search: s } : {}), ...(st ? { status: st } : {}) });
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/gotras?${params}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { fetch(1); }, []);

  const openAdd = () => { form.resetFields(); form.setFieldsValue({ status: 'approved' }); setEditing(null); setModal(true); };
  const openEdit = (row) => { form.setFieldsValue({ ...row }); setEditing(row); setModal(true); };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/admin/gotra-directory/gotras/${editing.id}`, values, { headers: auth() });
        notification.success({ message: 'Gotra Updated', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/admin/gotra-directory/gotras`, values, { headers: auth() });
        notification.success({ message: 'Gotra Added', placement: 'topRight' });
      }
      setModal(false); fetch(page);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete this gotra entry?', okType: 'danger', okText: 'Delete',
      content: 'This will also remove all Gotra-Kuldevi links.',
      onOk: async () => {
        await axios.delete(`${baseUrl}/admin/gotra-directory/gotras/${id}`, { headers: auth() });
        fetch(page);
      },
    });
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Gotra Name',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
          {r.name_en && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.name_en}</div>}
        </div>
      ),
    },
    { title: 'Pravara (Rishis)', dataIndex: 'pravara', ellipsis: true, render: v => v || '—' },
    { title: 'Veda', dataIndex: 'veda', width: 90, render: v => v ? <Tag color="blue">{v}</Tag> : '—' },
    { title: 'Kuldevi', dataIndex: 'kuldevi_text', ellipsis: true, render: v => v || '—' },
    {
      title: 'Status', width: 100,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.status === 'approved' ? 'green' : 'orange'}>{r.status}</Tag>
          {r.is_verified ? <Tag color="gold" icon={<StarOutlined />}>Verified</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Actions', width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col xs={24} sm={12} md={10}>
          <Input prefix={<SearchOutlined />} placeholder="Search gotra name, pravara..."
            value={search} onChange={e => { setSearch(e.target.value); fetch(1, e.target.value, statusFilter); }} allowClear />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Select style={{ width: '100%' }} placeholder="Status" value={statusFilter || undefined}
            onChange={v => { setStatusFilter(v || ''); fetch(1, search, v || ''); }} allowClear>
            <Option value="approved">Approved</Option>
            <Option value="pending">Pending</Option>
          </Select>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Gotra</Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={() => fetch(page)} />
        </Col>
      </Row>

      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 800 }}
        pagination={{ current: page, pageSize: 50, total, onChange: p => { setPage(p); fetch(p); }, showTotal: t => `Total ${t}` }} />

      <Modal open={modal} title={editing ? `Edit: ${editing?.name}` : 'Add New Gotra'}
        onCancel={() => setModal(false)} onOk={handleSave} confirmLoading={saving}
        width={780} okText={editing ? 'Update' : 'Add'}>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="name" label="Gotra Name (Hindi/Sanskrit)" rules={[{ required: true }]}>
                <Input placeholder="e.g. गौतम" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name_en" label="Name (English)">
                <Input placeholder="e.g. Gautam" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="pravara" label="Pravara — Rishis in Lineage (comma-separated)">
            <Input placeholder="e.g. अंगिरस, अयास्य, गौतम" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="pravara_count" label="Pravara Count">
                <Select placeholder="Select">
                  {[1,2,3,5,7].map(n => <Option key={n} value={n}>{n}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="veda" label="Veda">
                <Select placeholder="Select Veda" allowClear>
                  {VEDAS.map(v => <Option key={v} value={v}>{v}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="vedic_shakha" label="Vedic Shakha">
                <Input placeholder="e.g. Shakala" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="gotra_pravartaka" label="Gotra Pravartaka (Founder Rishi)">
                <Input placeholder="e.g. Maharishi Gautam" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="kulrishi" label="Kulrishi">
                <Input placeholder="e.g. Maharishi Vasishtha" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="kuldevi_text" label="Primary Kuldevi (free text — also link via Links tab)">
            <Input placeholder="e.g. Mata Sheetla Devi" />
          </Form.Item>
          <Form.Item name="surnames_associated" label="Surnames Associated (comma-separated or JSON array)">
            <Input placeholder='e.g. Sharma, Trivedi or ["Sharma","Trivedi"]' />
          </Form.Item>
          <Form.Item name="geographical_origin" label="Geographical Origin">
            <Input placeholder="e.g. Rajasthan, Haryana" />
          </Form.Item>
          <Form.Item name="mythological_reference" label="Mythological / Scriptural Reference">
            <TextArea rows={2} placeholder="Reference from puranas, vedas, etc." />
          </Form.Item>
          <Form.Item name="description" label="Description (Hindi)">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="description_en" label="Description (English)">
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="approved">Approved (Visible)</Option>
                  <Option value="pending">Pending (Hidden)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_verified" label="Admin Verified" valuePropName="checked">
                <Switch checkedChildren="Verified" unCheckedChildren="Unverified" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="admin_note" label="Admin Note (internal)">
            <Input placeholder="Internal notes..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── Kuldevi Tab ───────────────────────────────────────────────────────────────
function KuldevisTab() {
  const [data,       setData]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [modal,      setModal]      = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [photoUrl,   setPhotoUrl]   = useState('');
  const [uploading,  setUploading]  = useState(false);
  const [form] = Form.useForm();

  const fetch = useCallback(async (p = 1, s = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50, ...(s ? { search: s } : {}), status: 'all' });
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/kuldevis?${params}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(1); }, []);

  const openAdd = () => { form.resetFields(); form.setFieldsValue({ status: 'approved' }); setEditing(null); setPhotoUrl(''); setModal(true); };
  const openEdit = (row) => { form.setFieldsValue({ ...row }); setEditing(row); setPhotoUrl(row.photo_url || ''); setModal(true); };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('photo', file);
      const r = await axios.post(`${baseUrl}/admin/gotra-directory/upload-kuldevi-photo`, fd, { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } });
      if (r.data.status) { setPhotoUrl(r.data.url); notification.success({ message: 'Photo uploaded ✅', placement: 'topRight' }); }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally { setUploading(false); }
    return false;
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = { ...values, photo_url: photoUrl || null };
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/admin/gotra-directory/kuldevis/${editing.id}`, payload, { headers: auth() });
        notification.success({ message: 'Kuldevi Updated', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/admin/gotra-directory/kuldevis`, payload, { headers: auth() });
        notification.success({ message: 'Kuldevi Added', placement: 'topRight' });
      }
      setModal(false); fetch(page);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Kuldevi',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {r.photo_url
            ? <img src={r.photo_url} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
            : <div style={{ width: 44, height: 44, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <StarOutlined style={{ color: '#9ca3af' }} />
              </div>
          }
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
            {r.name_en && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.name_en}</div>}
            {r.also_known_as && <div style={{ fontSize: 11, color: '#9ca3af' }}>Also: {r.also_known_as}</div>}
          </div>
        </div>
      ),
    },
    { title: 'Temple', render: (_, r) => <span style={{ fontSize: 12 }}>{[r.temple_city, r.temple_state].filter(Boolean).join(', ') || '—'}</span> },
    { title: 'Deity Type', dataIndex: 'deity_type', width: 100, render: v => v ? <Tag color="purple">{v}</Tag> : '—' },
    {
      title: 'Status', width: 100,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.status === 'approved' ? 'green' : 'orange'}>{r.status}</Tag>
          {r.is_verified ? <Tag color="gold" icon={<StarOutlined />}>Verified</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Actions', width: 80,
      render: (_, r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col xs={24} sm={12} md={10}>
          <Input prefix={<SearchOutlined />} placeholder="Search kuldevi name, city, state..."
            value={search} onChange={e => { setSearch(e.target.value); fetch(1, e.target.value); }} allowClear />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Kuldevi</Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={() => fetch(page)} />
        </Col>
      </Row>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 700 }}
        pagination={{ current: page, pageSize: 50, total, onChange: p => { setPage(p); fetch(p); }, showTotal: t => `Total ${t}` }} />

      <Modal open={modal} title={editing ? `Edit: ${editing?.name}` : 'Add New Kuldevi'}
        onCancel={() => setModal(false)} onOk={handleSave} confirmLoading={saving}
        width={720} okText={editing ? 'Update' : 'Add'}>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="name" label="Kuldevi Name (Hindi)" rules={[{ required: true }]}>
                <Input placeholder="e.g. माता करणी देवी" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name_en" label="Name (English)">
                <Input placeholder="e.g. Mata Karni Devi" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="also_known_as" label="Also Known As (other names, comma-separated)">
            <Input placeholder="e.g. करनी माता, कर्णी माता" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="temple_location" label="Temple Location (Address)">
                <Input placeholder="Full address of main temple" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deity_type" label="Deity Type">
                <Input placeholder="e.g. Shakti, Mata, Devi, Bhavani" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="temple_city" label="Temple City">
                <Input placeholder="e.g. Deshnoke" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="temple_district" label="District">
                <Input placeholder="e.g. Bikaner" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="temple_state" label="State">
                <Input placeholder="e.g. Rajasthan" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="deity_description" label="Description of Deity">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="festival_info" label="Festival / Worship Info (when & how celebrated)">
            <TextArea rows={3} placeholder="e.g. नवरात्रि में विशेष पूजा, चैत्र मास में मेला..." />
          </Form.Item>
          <Form.Item name="mythological_story" label="Mythological Story / Origin">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="historical_reference" label="Historical Reference">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Kuldevi Photo">
            {photoUrl && (
              <div style={{ marginBottom: 8 }}>
                <img src={photoUrl} alt="kuldevi" style={{ width: 100, height: 100, borderRadius: 10, objectFit: 'cover', border: '2px solid #e5e7eb' }} />
              </div>
            )}
            <Upload beforeUpload={uploadPhoto} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading} size="small">
                {photoUrl ? 'Change Photo' : 'Upload Photo'}
              </Button>
            </Upload>
            {photoUrl && (
              <Button size="small" danger style={{ marginLeft: 8 }} onClick={() => setPhotoUrl('')}>Remove</Button>
            )}
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="approved">Approved</Option>
                  <Option value="pending">Pending</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_verified" label="Admin Verified" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="admin_note" label="Admin Note (internal)">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── Kulrishi Tab ──────────────────────────────────────────────────────────────
function KulrishisTab() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [form] = Form.useForm();

  const fetch = useCallback(async (s = search) => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/kulrishis`, { headers: auth() });
      if (r.data.status) {
        const rows = r.data.result || [];
        setData(s ? rows.filter(x => x.name?.toLowerCase().includes(s.toLowerCase()) || x.name_en?.toLowerCase().includes(s.toLowerCase())) : rows);
      }
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, []);

  const openAdd  = () => { form.resetFields(); form.setFieldsValue({ status: 'approved' }); setEditing(null); setModal(true); };
  const openEdit = (row) => { form.setFieldsValue({ ...row }); setEditing(row); setModal(true); };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/admin/gotra-directory/kulrishis/${editing.id}`, values, { headers: auth() });
        notification.success({ message: 'Kulrishi Updated', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/admin/gotra-directory/kulrishis`, values, { headers: auth() });
        notification.success({ message: 'Kulrishi Added', placement: 'topRight' });
      }
      setModal(false); fetch();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Kulrishi Name',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
          {r.name_en && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.name_en}</div>}
          {r.linked_gotras_text && <div style={{ fontSize: 11, color: '#9ca3af' }}>Gotras: {r.linked_gotras_text}</div>}
        </div>
      ),
    },
    { title: 'Veda', dataIndex: 'veda', width: 100, render: v => v ? <Tag color="blue">{v}</Tag> : '—' },
    { title: 'Lineage', dataIndex: 'lineage_description', ellipsis: true, width: 200, render: v => v || '—' },
    {
      title: 'Status', width: 100,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.status === 'approved' ? 'green' : 'orange'}>{r.status}</Tag>
          {r.is_verified ? <Tag color="gold" icon={<StarOutlined />}>Verified</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Actions', width: 80,
      render: (_, r) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />,
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col xs={24} sm={12} md={10}>
          <Input prefix={<SearchOutlined />} placeholder="Search kulrishi name..."
            value={search} onChange={e => { setSearch(e.target.value); fetch(e.target.value); }} allowClear />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Kulrishi</Button>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={() => fetch()} /></Col>
      </Row>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 700 }}
        pagination={{ pageSize: 50, showTotal: t => `Total ${t}` }} />

      <Modal open={modal} title={editing ? `Edit: ${editing?.name}` : 'Add New Kulrishi'}
        onCancel={() => setModal(false)} onOk={handleSave} confirmLoading={saving}
        width={680} okText={editing ? 'Update' : 'Add'}>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="name" label="Kulrishi Name (Hindi/Sanskrit)" rules={[{ required: true }]}>
                <Input placeholder="e.g. महर्षि वशिष्ठ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name_en" label="Name (English)">
                <Input placeholder="e.g. Maharishi Vasishtha" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="veda" label="Veda">
            <Select allowClear placeholder="Select Veda">
              {VEDAS.map(v => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="lineage_description" label="Lineage / Vanshavali Description">
            <TextArea rows={3} placeholder="Description of this rishi's lineage and significance..." />
          </Form.Item>
          <Form.Item name="reference_texts" label="Reference Texts / Scriptures">
            <TextArea rows={2} placeholder="Which scriptures or texts mention this rishi? (e.g. Rigveda 7.33, Mahabharata)" />
          </Form.Item>
          <Form.Item name="linked_gotras_text" label="Linked Gotras (free text, comma-separated)">
            <Input placeholder="e.g. वशिष्ठ, शक्ति, पराशर" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="approved">Approved (Visible)</Option>
                  <Option value="pending">Pending (Hidden)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_verified" label="Admin Verified" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="admin_note" label="Admin Note (internal)">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── Links Tab ─────────────────────────────────────────────────────────────────
function LinksTab() {
  const [links,    setLinks]    = useState([]);
  const [gotras,   setGotras]   = useState([]);
  const [kuldevis, setKuldevis] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');
  const [form] = Form.useForm();

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/links`, { headers: auth() });
      if (r.data.status) setLinks(r.data.result || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchGotras = async () => {
    const r = await axios.get(`${baseUrl}/admin/gotra-directory/gotras?limit=500&status=approved`, { headers: auth() });
    if (r.data.status) setGotras(r.data.result || []);
  };
  const fetchKuldevis = async () => {
    const r = await axios.get(`${baseUrl}/admin/gotra-directory/kuldevis?limit=500&status=approved`, { headers: auth() });
    if (r.data.status) setKuldevis(r.data.result || []);
  };

  useEffect(() => { fetchLinks(); fetchGotras(); fetchKuldevis(); }, []);

  const handleAdd = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await axios.post(`${baseUrl}/admin/gotra-directory/links`, values, { headers: auth() });
      notification.success({ message: 'Link Created', placement: 'topRight' });
      setModal(false); form.resetFields(); fetchLinks();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Remove this Gotra-Kuldevi link?', okType: 'danger', okText: 'Remove',
      onOk: async () => {
        await axios.delete(`${baseUrl}/admin/gotra-directory/links/${id}`, { headers: auth() });
        fetchLinks();
      },
    });
  };

  const filtered = links.filter(l =>
    !search || l.gotra_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.kuldevi_name?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    { title: 'Gotra', dataIndex: 'gotra_name', render: v => <strong>{v}</strong> },
    { title: 'Kuldevi', dataIndex: 'kuldevi_name', render: v => <span style={{ color: '#7c3aed' }}>{v}</span> },
    { title: 'Primary', dataIndex: 'is_primary', width: 80, render: v => v ? <Tag color="gold">Primary</Tag> : <Tag>Secondary</Tag> },
    { title: 'Verified', dataIndex: 'is_verified', width: 80, render: v => v ? <CheckCircleOutlined style={{ color: '#16a34a' }} /> : '—' },
    { title: 'Notes', dataIndex: 'link_notes', ellipsis: true, render: v => v || '—' },
    {
      title: 'Actions', width: 80,
      render: (_, r) => <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} />,
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col xs={24} sm={12} md={10}>
          <Input prefix={<SearchOutlined />} placeholder="Search gotra or kuldevi name..."
            value={search} onChange={e => setSearch(e.target.value)} allowClear />
        </Col>
        <Col>
          <Button type="primary" icon={<LinkOutlined />} onClick={() => { form.resetFields(); setModal(true); }}>
            Link Gotra ↔ Kuldevi
          </Button>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={fetchLinks} /></Col>
      </Row>
      <Table dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 600 }} />

      <Modal open={modal} title="Create Gotra ↔ Kuldevi Link"
        onCancel={() => setModal(false)} onOk={handleAdd} confirmLoading={saving} okText="Link">
        <Form form={form} layout="vertical" initialValues={{ is_primary: false, is_verified: true }}>
          <Form.Item name="gotra_id" label="Gotra" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select gotra...">
              {gotras.map(g => <Option key={g.id} value={g.id}>{g.name} {g.name_en ? `(${g.name_en})` : ''}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="kuldevi_id" label="Kuldevi" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select kuldevi...">
              {kuldevis.map(k => <Option key={k.id} value={k.id}>{k.name} {k.name_en ? `(${k.name_en})` : ''}</Option>)}
            </Select>
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="is_primary" label="Is Primary Kuldevi?" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_verified" label="Verified Link?" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="link_notes" label="Notes (source or reason for this link)">
            <TextArea rows={2} placeholder="How do we know this connection? Reference source..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── Contributions Tab ─────────────────────────────────────────────────────────
function ContributionsTab() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState('pending');
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [viewing, setViewing] = useState(null);
  const [note,    setNote]    = useState('');
  const [saving,  setSaving]  = useState(false);

  const fetch = useCallback(async (p = 1, s = status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, status: s });
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/contributions?${params}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch {} finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetch(1); }, []);

  const handleReview = async (id, action) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${baseUrl}/admin/gotra-directory/contributions/${id}`, { action, admin_note: note }, { headers: auth() });
      const linked = r.data?.linked_entity_id;
      notification.success({
        message: action === 'approve' ? `Approved ✅${linked ? ` — Entry created in directory (ID: ${linked})` : ''}` : 'Rejected',
        placement: 'topRight', duration: 5,
      });
      setViewing(null); setNote(''); fetch(page);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const TYPE_COLOR = {
    new_gotra: '#7a0000', new_kuldevi: '#7c3aed', link_info: '#0891b2',
    correction: '#d97706', kulrishi_info: '#16a34a', general_info: '#6b7280',
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    { title: 'Type', dataIndex: 'contribution_type', width: 130, render: v => <Tag color={TYPE_COLOR[v] || '#6b7280'}>{v?.replace(/_/g,' ')}</Tag> },
    { title: 'About Gotra', dataIndex: 'about_gotra', ellipsis: true, render: v => v || '—' },
    { title: 'About Kuldevi', dataIndex: 'about_kuldevi', ellipsis: true, render: v => v || '—' },
    { title: 'Submitter', render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.submitter_name}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{r.submitter_phone}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{r.submitter_village}{r.submitter_gotra ? ` · ${r.submitter_gotra}` : ''}</div>
      </div>
    )},
    { title: 'Date', dataIndex: 'created_at', width: 100, render: v => v?.slice(0, 10) },
    { title: 'Status', dataIndex: 'status', width: 90, render: v => <Tag color={v === 'approved' ? 'green' : v === 'rejected' ? 'red' : 'orange'}>{v}</Tag> },
    {
      title: 'Actions', width: 120,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setViewing(r); setNote(''); }}>View</Button>
          {r.status === 'pending' && (
            <>
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
                onClick={() => handleReview(r.id, 'approve')}>OK</Button>
              <Button size="small" danger icon={<CloseCircleOutlined />}
                onClick={() => handleReview(r.id, 'reject')}>No</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col>
          <Select value={status} onChange={v => { setStatus(v); fetch(1, v); }} style={{ width: 140 }}>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={() => fetch(page)} /></Col>
      </Row>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 800 }}
        pagination={{ current: page, pageSize: 30, total, onChange: p => { setPage(p); fetch(p); }, showTotal: t => `Total ${t}` }} />

      <Modal open={!!viewing} title="Contribution Details"
        onCancel={() => setViewing(null)}
        footer={[
          viewing?.status === 'pending' && (
            <Button key="r" danger loading={saving} onClick={() => handleReview(viewing.id, 'reject')}>Reject</Button>
          ),
          viewing?.status === 'pending' && (
            <Button key="a" type="primary" loading={saving} style={{ background: '#16a34a' }}
              onClick={() => handleReview(viewing.id, 'approve')}>Approve & Add to Directory</Button>
          ),
          <Button key="c" onClick={() => setViewing(null)}>Close</Button>,
        ].filter(Boolean)} width={600}>
        {viewing && (
          <div>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Type"><Tag>{viewing.contribution_type?.replace(/_/g,' ')}</Tag></Descriptions.Item>
              <Descriptions.Item label="About Gotra">{viewing.about_gotra || '—'}</Descriptions.Item>
              <Descriptions.Item label="About Kuldevi">{viewing.about_kuldevi || '—'}</Descriptions.Item>
              <Descriptions.Item label="Submitter">{viewing.submitter_name} · {viewing.submitter_phone}</Descriptions.Item>
              <Descriptions.Item label="Village">{viewing.submitter_village || '—'}</Descriptions.Item>
              <Descriptions.Item label="Their Gotra">{viewing.submitter_gotra || '—'}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 12, background: '#f9fafb', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>SUBMITTED DATA</div>
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {(() => { try { return JSON.stringify(JSON.parse(viewing.submitted_data), null, 2); } catch { return viewing.submitted_data; } })()}
              </pre>
            </div>
            {viewing.status === 'pending' && (
              <div style={{ marginTop: 12 }}>
                <Input.TextArea rows={2} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Admin note (optional for approval, explain for rejection)..." />
              </div>
            )}
            {viewing.admin_note && (
              <div style={{ marginTop: 8, color: '#dc2626', fontSize: 12 }}>Admin note: {viewing.admin_note}</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Comments Tab ──────────────────────────────────────────────────────────────
function CommentsTab() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState('pending');
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [saving,  setSaving]  = useState(false);

  const fetch = useCallback(async (p = 1, s = status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, status: s });
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/comments?${params}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch {} finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetch(1); }, []);

  const handleReview = async (id, action) => {
    setSaving(true);
    try {
      await axios.patch(`${baseUrl}/admin/gotra-directory/comments/${id}`, { action }, { headers: auth() });
      notification.success({ message: action === 'approve' ? 'Comment Approved ✅' : 'Rejected', placement: 'topRight' });
      fetch(page);
    } catch {} finally { setSaving(false); }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    { title: 'On', render: (_, r) => <Tag color={r.entity_type === 'gotra' ? '#7a0000' : '#7c3aed'}>{r.entity_type} #{r.entity_id}</Tag> },
    { title: 'Commenter', render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.commenter_name}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{r.commenter_village}{r.commenter_gotra ? ` · ${r.commenter_gotra}` : ''}</div>
      </div>
    )},
    { title: 'Comment', dataIndex: 'comment_text', ellipsis: true },
    { title: 'Date', dataIndex: 'created_at', width: 100, render: v => v?.slice(0, 10) },
    { title: 'Status', dataIndex: 'status', width: 90, render: v => <Tag color={v === 'approved' ? 'green' : v === 'rejected' ? 'red' : 'orange'}>{v}</Tag> },
    {
      title: 'Actions', width: 130,
      render: (_, r) => r.status === 'pending' ? (
        <Space>
          <Button size="small" type="primary" icon={<CheckCircleOutlined />}
            style={{ background: '#16a34a', borderColor: '#16a34a' }}
            loading={saving} onClick={() => handleReview(r.id, 'approve')}>Approve</Button>
          <Button size="small" danger icon={<CloseCircleOutlined />}
            loading={saving} onClick={() => handleReview(r.id, 'reject')}>Reject</Button>
        </Space>
      ) : '—',
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col>
          <Select value={status} onChange={v => { setStatus(v); fetch(1, v); }} style={{ width: 140 }}>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={() => fetch(page)} /></Col>
      </Row>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 700 }}
        pagination={{ current: page, pageSize: 30, total, onChange: p => { setPage(p); fetch(p); }, showTotal: t => `Total ${t}` }} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function GotraDirectoryList() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/gotra-directory/stats`, { headers: auth() });
      if (r.data.status) setStats(r.data.result);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, color: '#7a0000' }}>
            <BookOutlined style={{ marginRight: 10, color: '#7c3aed' }} />
            Gotra Directory — Community Knowledge Base
          </h2>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
            Multi-dimensional directory of Gotras, Kuldevis, Kulrishis and their linkages
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchStats} loading={loading}>Refresh Stats</Button>
      </div>

      <StatsRow stats={stats} />

      <Card bodyStyle={{ padding: '0 0' }}>
        <Tabs defaultActiveKey="gotras" tabBarStyle={{ paddingLeft: 16, paddingRight: 16 }}>
          <TabPane tab={<span><BookOutlined />Gotras</span>} key="gotras">
            <div style={{ padding: 16 }}><GotrasTab /></div>
          </TabPane>
          <TabPane tab={<span><StarOutlined />Kuldevis</span>} key="kuldevis">
            <div style={{ padding: 16 }}><KuldevisTab /></div>
          </TabPane>
          <TabPane tab={<span><UserOutlined />Kulrishis</span>} key="kulrishis">
            <div style={{ padding: 16 }}><KulrishisTab /></div>
          </TabPane>
          <TabPane tab={<span><LinkOutlined />Gotra ↔ Kuldevi Links</span>} key="links">
            <div style={{ padding: 16 }}><LinksTab /></div>
          </TabPane>
          <TabPane tab={
            <span>
              <UploadOutlined />
              Contributions
              {stats?.contributions?.pending > 0 && (
                <Badge count={stats.contributions.pending} size="small" style={{ marginLeft: 6 }} />
              )}
            </span>
          } key="contributions">
            <div style={{ padding: 16 }}><ContributionsTab /></div>
          </TabPane>
          <TabPane tab={
            <span>
              <CommentOutlined />
              Comments
              {stats?.comments?.pending > 0 && (
                <Badge count={stats.comments.pending} size="small" style={{ marginLeft: 6 }} />
              )}
            </span>
          } key="comments">
            <div style={{ padding: 16 }}><CommentsTab /></div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
